import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../components/Button";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { ToolStatus } from "../components/Chat/ToolStatus";
import type { AgentAction } from "../components/LiveBrowser/useAgentControl";
import type { DriveCommand } from "../lib/webpageDebugBridge";

export type WebpageDebugArgs = {
  drive: (
    step: string,
    command: DriveCommand
  ) => Promise<{
    dom?: string;
    console?: { level: string; text: string }[];
    actionErrors?: { index: number; error: string }[];
    image?: { mediaType: string; data: string };
    error?: string;
  }>;
};

type Input = { step?: string; actions?: AgentAction[]; selector?: string };

const DOM_LIMIT = 80_000;

// Front-biased: for rendered markup the meaningful content is at the top of the
// body, so keep the start and drop the tail — unlike logs, where the end matters.
function truncate(text: string | undefined): {
  text: string | null;
  truncated: boolean;
} {
  if (!text) return { text: null, truncated: false };
  if (text.length <= DOM_LIMIT) return { text, truncated: false };
  const omitted = text.length - DOM_LIMIT;
  return {
    text: `${text.slice(0, DOM_LIMIT)}\n\n[… ${omitted.toLocaleString()} chars truncated …]`,
    truncated: true,
  };
}

function header(label: string) {
  return function Header({ result, interrupted }: ToolUseProps) {
    const output = result?.output as
      | { ok?: boolean; error?: string }
      | undefined;
    if (!output) {
      return <ToolStatus icon="earth" text={label} pending={!interrupted} />;
    }
    if (output.ok === false) {
      return (
        <ToolStatus
          icon="circle-off"
          iconColor="var(--red-600)"
          text={output.error ?? "Failed"}
        />
      );
    }
    return (
      <ToolStatus
        icon="check-filled"
        iconColor="var(--green-600)"
        text={`${label} — done`}
      />
    );
  };
}

// Fires the drive once when the pending prompt mounts, then resolves the tool.
function DriveRunner({
  args,
  step,
  command,
  onResult,
}: {
  args: WebpageDebugArgs;
  step: string | undefined;
  command: DriveCommand;
  onResult: (output: unknown, isError: boolean) => Promise<boolean>;
}) {
  const started = useRef(false);
  const [retry, setRetry] = useState<{
    output: unknown;
    isError: boolean;
  } | null>(null);
  const submit = useCallback(
    async (output: unknown, isError: boolean) => {
      setRetry(null);
      if (!(await onResult(output, isError))) setRetry({ output, isError });
    },
    [onResult]
  );
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!step) {
      void submit({ ok: false, error: "No step specified" }, true);
      return;
    }
    void (async () => {
      try {
        const result = await args.drive(step, command);
        if (result.error) {
          await submit({ ok: false, error: result.error }, true);
          return;
        }
        const dom = truncate(result.dom);
        await submit(
          {
            ok: true,
            dom: dom.text ?? undefined,
            domTruncated: dom.truncated,
            console: result.console,
            actionErrors: result.actionErrors,
            // Field name the model-message serializer reads to send these as
            // real image content rather than base64 text.
            imageParts: result.image ? [result.image] : undefined,
          },
          false
        );
      } catch (e) {
        await submit(
          { ok: false, error: e instanceof Error ? e.message : String(e) },
          true
        );
      }
    })();
  }, [args, command, step, submit]);
  return retry ? (
    <Button onClick={() => void submit(retry.output, retry.isError)}>
      Retry saving result
    </Button>
  ) : null;
}

export const inspectRenderedPage = (args: WebpageDebugArgs): ChatTool => ({
  name: "inspectRenderedPage",
  Header: header("Inspecting rendered page"),
  alwaysLoadOutput: true,
  renderPendingPrompt: ({ input, onResult }) => {
    const parsed = (input as Input | null) ?? {};
    return (
      <DriveRunner
        args={args}
        step={parsed.step}
        command={{ kind: "inspect", selector: parsed.selector }}
        onResult={onResult}
      />
    );
  },
});

export const getPageConsole = (args: WebpageDebugArgs): ChatTool => ({
  name: "getPageConsole",
  Header: header("Reading page console"),
  alwaysLoadOutput: true,
  renderPendingPrompt: ({ input, onResult }) => (
    <DriveRunner
      args={args}
      step={(input as Input | null)?.step}
      command={{ kind: "console" }}
      onResult={onResult}
    />
  ),
});

export const interactWithPage = (args: WebpageDebugArgs): ChatTool => ({
  name: "interactWithPage",
  Header: header("Interacting with page"),
  alwaysLoadOutput: true,
  renderPendingPrompt: ({ input, onResult }) => {
    const parsed = (input as Input | null) ?? {};
    return (
      <DriveRunner
        args={args}
        step={parsed.step}
        command={{
          kind: "interact",
          actions: parsed.actions ?? [],
          selector: parsed.selector,
        }}
        onResult={onResult}
      />
    );
  },
});

function ScreenshotBody({ result }: ToolUseProps) {
  const output = result?.output as
    | { imageParts?: { mediaType: string; data: string }[] }
    | undefined;
  const image = output?.imageParts?.[0];
  if (!image) return null;
  return (
    <img
      src={`data:${image.mediaType};base64,${image.data}`}
      alt="Page screenshot"
      style={{
        maxWidth: "100%",
        borderRadius: 6,
        border: "1px solid var(--alpha8)",
      }}
    />
  );
}

export const screenshotPage = (args: WebpageDebugArgs): ChatTool => ({
  name: "screenshotPage",
  Header: header("Taking screenshot"),
  Body: ScreenshotBody,
  alwaysLoadOutput: true,
  renderPendingPrompt: ({ input, onResult }) => (
    <DriveRunner
      args={args}
      step={(input as Input | null)?.step}
      command={{ kind: "screenshot" }}
      onResult={onResult}
    />
  ),
});
